import {catchError, map, Observable, of, ReplaySubject, tap} from "rxjs";
import {environment} from "@env/environment";
import {HttpClient, HttpParams} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {StockStatus} from "@core/services/attestations/stock-status/stock-status.model";
import {SpringPage} from "@core/services/pricing/shared/spring-page.model";

@Injectable({
  providedIn: 'root'
})
export class StockStatusService {

  baseUrl = environment.numeric_attestations_url + '/stock';
  private _stockStatus: ReplaySubject<StockStatus> = new ReplaySubject<StockStatus>(1);
  private _stockStatuses: ReplaySubject<StockStatus[]> = new ReplaySubject<StockStatus[]>(1);

  set stockStatus(value: StockStatus) {
    this._stockStatus.next(value);
  }

  get stockStatus$() {
    return this._stockStatus.asObservable();
  }

  set stockStatuses(value: StockStatus[]) {
    this._stockStatuses.next(value);
  }

  get stockStatuses$() {
    return this._stockStatuses.asObservable();
  }

  constructor(
    private _httpClient: HttpClient
  ) {
  }

  create(stockStatus: StockStatus): Observable<StockStatus> {
    return this._httpClient.post<StockStatus>(`${this.baseUrl}`, stockStatus)
      .pipe(
        tap((response: StockStatus) => {
          this.stockStatus = new StockStatus(response);
          return this.stockStatus;
        }),
        catchError(() => of({} as StockStatus))
      );
  }

  getAll(): Observable<StockStatus[]> {
    return this.getPage(0, 1000).pipe(
      map(page => page.content),
      catchError(() => of([]))
    );
  }

  getPage(page = 0, size = 10, sort?: string, referenceUuid?: string, exerciseYear?: number): Observable<SpringPage<StockStatus>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (sort) params = params.set('sort', sort);
    if (referenceUuid) params = params.set('referenceUuid', referenceUuid);
    if (exerciseYear != null) params = params.set('exerciseYear', exerciseYear.toString());
    return this._httpClient.get<SpringPage<StockStatus>>(this.baseUrl, {params})
      .pipe(
        map((response: SpringPage<StockStatus>) => ({
          ...response,
          content: (response?.content || []).map(item => new StockStatus(item))
        })),
        tap(p => this._stockStatuses.next(p.content)),
        catchError(() => of({
          content: [], totalElements: 0, totalPages: 0, size,
          number: page, first: true, last: true, numberOfElements: 0, empty: true
        } as SpringPage<StockStatus>))
      );
  }

  update(stockStatus: StockStatus): Observable<StockStatus> {
    return this._httpClient.put<StockStatus>(`${this.baseUrl}/${stockStatus.uuid}`, stockStatus)
      .pipe(
        tap((response: StockStatus) => {
          this.stockStatus = response;
          return response;
        }),
        catchError(() => of({} as StockStatus))
      );
  }

  delete(id: string): Observable<void> {
    return this._httpClient.delete<void>(`${this.baseUrl}/${id}`)
      .pipe(
        tap(() => {
          this.stockStatuses$
            .pipe(
              map(stockStatuses => stockStatuses.filter(stockStatus => stockStatus.uuid !== id))
            ).subscribe(updatedStockStatuses => this.stockStatuses = updatedStockStatuses);
        }),
        catchError(() => of())
      );
  }

  /**
   * Get hierarchy with stock information for a given attestation reference.
   * The product selection has been deprecated backend-side, so it is no longer sent.
   * Endpoint: GET /app/stock/for-hierarchy?attestation-reference={attestationReferenceUuid}&company={companyUuid}
   */
  getHierarchyWithStock(attestationReferenceUuid: string, companyUuid?: string): Observable<any> {
    let params = new HttpParams()
      .set('attestation-reference', attestationReferenceUuid);

    if (companyUuid) {
      params = params.set('company', companyUuid);
    }

    return this._httpClient.get<any>(`${this.baseUrl}/for-hierarchy`, { params })
      .pipe(
        catchError(() => of(null))
      );
  }

}
